import _                                             from 'underscore';
import classNames                                    from 'classnames';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Header, Icon, LabelProps }                  from 'semantic-ui-react';
import AnimateHeight                                 from 'react-animate-height';

import { generateCustomEventDetails, triggerResizeEvent } from '../../util';
import { CONSTANTS }                                      from '../../constants';
import { Flexbox, FlexItem, StyledContainer }             from '../containers';
import { TotalActiveLabel, TotalActiveLabelProps }        from '../components/labels';
import { EVENT_TYPE_STATES, EVENT_TYPES }                 from '../../constants/EventsEnums';
import { FlexAlign, SemanticUIColor, Size }               from '../../types/componentPropTypes';
import { ClassValue }                                     from 'classnames/types';
import { SystemEvent }                                    from '../../types/events';


export interface CollapsibleContentContainerProps {
    containerEventId?: string,
    onToggle?: CollapsibleContentContainerToggleFunction,
    title?: string | ReactNode,
    label?: string | LabelProps,
    outlineColor?: SemanticUIColor,
    selectedColor?: SemanticUIColor,
    inactiveColor?: SemanticUIColor,
    invertColorOnSelect?: boolean,
    borderless?: boolean,
    noHoverCursor?: boolean,
    noHoverOutline?: boolean,
    isActive?: boolean,
    fadeIn?: boolean,
    headerComponent?: FC<CollapsibleContentContainerFuncComponentProps> | React.ReactNode,
    appendedHeaderComponent?: FC<CollapsibleContentContainerFuncComponentProps> | React.ReactNode,
    contentClasses?: ClassValue,
    containerClasses?: ClassValue,
    collapseDurationMS?: number,
    animated?: boolean,
    children?: React.ReactNode | CollapsibleContentContainerChildFunction,
}

export interface CollapsibleContentContainerFuncComponentProps {
    onClick: any,
    isActive: boolean
}

export interface CollapsibleContentContainerChildFunction {
    ( onClick: Function, isActive: boolean ): React.ReactNode
}

export interface CollapsibleContentContainerToggleFunction {
    ( nextActiveState: boolean, collapseDurationMS: number, e: SystemEvent ): void | React.ReactNode
}

const defaultProps: CollapsibleContentContainerProps = {
    containerEventId:        null,
    onToggle:                null,
    title:                   'Items',
    label:                   null,
    outlineColor:            SemanticUIColor.PURPLE,
    selectedColor:           SemanticUIColor.PURPLE,
    inactiveColor:           null,
    invertColorOnSelect:     true,
    borderless:              false,
    noHoverCursor:           false,
    noHoverOutline:          false,
    isActive:                false,
    fadeIn:                  true,
    headerComponent:         null,
    appendedHeaderComponent: null,
    contentClasses:          null,
    containerClasses:        null,
    collapseDurationMS:      CONSTANTS.UI.DISPLAY.EFFECT,
    animated:                true,
};

const CollapsibleContentContainer: FC<CollapsibleContentContainerProps> = ( props ) => {

    let { inactiveColor, selectedColor, outlineColor } = props,
        HeaderComponent: any                           = props.headerComponent,
        AppendedHeaderComponent: any                   = props.appendedHeaderComponent;

    let [ isActive, setIsActive ] = useState( props.isActive ),
        hasLabel                  = ( !_.isNull( props.label ) );

    // when outside props update
    useEffect( () => {

        setIsActive( props.isActive );

        setTimeout( () => triggerResizeEvent( generateCustomEventDetails(
            EVENT_TYPES.COMPONENT_TOGGLE,
            EVENT_TYPE_STATES[ props.isActive ? 'ON' : 'OFF' ], {
                component:   'CollapsibleContentContainer',
                id:          props.containerEventId ?? null,
                toggleState: props.isActive
            }
        ) ), props.animated ? props.collapseDurationMS : 0 );

    }, [ props.isActive ] )

    let handleContainerClicked = ( e: SystemEvent ) => {

        // set active
        setIsActive( !isActive );

        if( _.isFunction( props.onToggle ) ){

            props.onToggle( !isActive, props.collapseDurationMS, e );
        }
    };

    let handleContainerStartAnimation = ( e: ( { newHeight: number } ) ): void => {

        triggerResizeEvent( generateCustomEventDetails(
            EVENT_TYPES.COMPONENT_ANIMATION_HEIGHT,
            EVENT_TYPE_STATES.START, {
                newHeight: e.newHeight,
                component: 'CollapsibleContentContainer',
                id:        props.containerEventId ?? null,
            }
        ) )
    }

    let handleContainerEndAnimation = ( e: ( { newHeight: number } ) ): void => {

        triggerResizeEvent( generateCustomEventDetails(
            EVENT_TYPES.COMPONENT_ANIMATION_HEIGHT,
            EVENT_TYPE_STATES.END, {
                newHeight: e.newHeight,
                component: 'CollapsibleContentContainer',
                id:        props.containerEventId ?? null,
            } )
        );
    }

    let containerClasses = classNames( {
            'border--colored-lt-gray':         !props.borderless,
            'border-sm-bottom':                !props.borderless,
            'collapsible-container':           true,
            'collapsible-container--isOpen':   isActive,
            'collapsible-container--isClosed': !isActive,
        }, props.containerClasses ),
        headerClasses    = classNames( {
            'cursor--pointer':              !props.noHoverCursor,
            'outline--hover':               !props.noHoverOutline,
            'collapsible-header':           true,
            'collapsible-header--isOpen':   isActive,
            'collapsible-header--isClosed': !isActive,
        } ),
        contentClasses   = classNames( {
            'padding-md padding-lg-left':    !props.contentClasses,
            'collapsible-content':           true,
            'collapsible-content--isOpen':   isActive,
            'collapsible-content--isClosed': !isActive,
        }, props.contentClasses );

    let internalContent = ( _.isFunction( props.children ) )
                          ? <div className={ contentClasses }>{ props.children( handleContainerClicked, isActive ) }</div>
                          : <div className={ contentClasses }>{ props.children }</div>;

    return (

        <div className={ containerClasses }>

            { props.headerComponent ? (

                <HeaderComponent onClick={ handleContainerClicked } isActive={ isActive }/>

            ) : (

                  <StyledContainer
                      onClick={ handleContainerClicked }
                      outlineSize={ props.noHoverOutline ? null : Size.MD }
                      outlineColor={ props.noHoverOutline ? null : selectedColor || outlineColor }
                      className={ headerClasses }>

                      <StyledContainer padding={ [ Size.MD, Size.MD ] } bgColor={ isActive ? selectedColor : null }>

                          <Flexbox align={ FlexAlign.CENTER }>

                              <FlexItem noGrow noShrink className={ 'margin-md-right' }>

                                  <Icon inverted={ isActive && props.invertColorOnSelect }
                                        name={ isActive ? 'chevron down' : 'chevron right' }
                                        color={ isActive ? null : inactiveColor }
                                        fitted/>

                              </FlexItem>
                              <FlexItem basis={ 50 }>

                                  <Header as={ 'h3' }
                                          color={ isActive ? null : inactiveColor }
                                          inverted={ isActive && props.invertColorOnSelect }
                                          content={ props.title }/>

                              </FlexItem>

                              { hasLabel && (
                                  <FlexItem noGrow align={ FlexAlign.END }>

                                      { _.isFunction( props.label )
                                        ? props.label()
                                        : <TotalActiveLabel completedColor={ selectedColor } { ...props.label as TotalActiveLabelProps } />
                                      }
                                  </FlexItem>
                              ) }

                          </Flexbox>

                          { props.appendedHeaderComponent &&
                            <AppendedHeaderComponent onClick={ handleContainerClicked } isActive={ isActive }/> }

                      </StyledContainer>

                  </StyledContainer>
              ) }

            { props.animated ? (
                <StyledContainer className={ classNames( {
                    'fade-in': isActive && props.fadeIn,
                } ) }>

                    <AnimateHeight
                        height={ isActive ? 'auto' : 0 }
                        duration={ props.collapseDurationMS }
                        easing={ 'ease-in-out' }
                        onAnimationEnd={ handleContainerStartAnimation }
                        onAnimationStart={ handleContainerEndAnimation }
                        animateOpacity={ true }>

                        { internalContent }

                    </AnimateHeight>

                </StyledContainer>

            ) : ( isActive && internalContent ) }

        </div>
    )
};

CollapsibleContentContainer.defaultProps = defaultProps;

export { CollapsibleContentContainer }
